export const AI_AGENT_REPORT_TYPE = 'AI Agent';

export const AI_AGENT_OVERVIEW_SECTION_KEYS = {
    KEY_METRICS: 'keyMetrics',
    QUALITY_METRICS: 'qualityMetrics',
    TRENDS_AND_FEEDBACK: 'trendsAndFeedback',
    SUGGESTED_PROMPTS: 'suggestedPrompts',
    MODEL_USAGE: 'modelUsage',
    TOP_ENTITIES: 'topEntities',
    PROMPTS: 'prompts',
};

export const AI_AGENT_ICON_TYPE = 'agentic';

export const SIMILARITY_THRESHOLD = 0.6;

export const SUPPORT_LINK = 'https://support.pendo.io/hc/en-us/articles/41103585431707';

export const CONVERSATION_MODE_SUPPORT_LINK = 'https://support.pendo.io/hc/en-us/articles/41103585431707#h_01K5SBRA8V76QY9AGFMAAHZ4JM';

export const LEARN_MORE_LINK = 'https://support.pendo.io/hc/en-us/articles/45209760982683-Overview-of-Agent-Analytics';

export const SECURITY_LINK = 'https://support.pendo.io/hc/en-us/articles/41103184012315-Data-collection-and-security-for-Agent-Analytics-beta';

export const WIDGET_TYPE_NAME_MAP = {
    keyMetric: 'Key Metrics',
    useCaseTable: 'Emergent Use Cases',
    promptsTable: 'Prompts',
    trackedUseCasesTable: 'Tracked Use Cases',
    detectIssuesTable: 'Emergent Issues',
};

export const CLUSTER_BLADE_TYPE_MAP = {
    account: 'accounts',
    visitor: 'visitors',
    instances: 'instances',
    conversation: 'conversations',
    prompt: 'prompts',
    ragePrompt: 'rage prompts',
    error: 'errors',
    unsupported: 'unsupported requests',
};

export const CLUSTER_BLADE_TYPES = ['useCase', 'trackedUseCases', 'detectIssues', 'trackedIssues', 'feedback', 'tools', 'suggestedPrompts'];

// Sampling threshold for AI agent use cases table
export const SAMPLING_THRESHOLD = 10000;

export const DEFAULT_CLUSTER_GRANULARITY = 3;

export const MILLISECONDS_IN_DAY = 24 * 60 * 60 * 1000;

export const SETUP_BANNER_MIN_AGE_DAYS = 14;

export const SETUP_BANNER_MIN_AGE_MS = SETUP_BANNER_MIN_AGE_DAYS * MILLISECONDS_IN_DAY;

export const FIXTURE_BASED_IN_DASHBOARD_TEMPLATE_TYPES = ['useCaseTable', 'trackedUseCasesTable', 'detectIssuesTable'];

export const TRACKED_USE_CASE_STATUS = {
    STABLE: 'Stable',
    PROCESSING: 'Processing',
};

// Maximum number of active tracked use cases allowed per application
export const MAX_ACTIVE_TRACKED_USE_CASES_LIMIT = 200;

// Maximum number of detection rules allowed per tracked issue
export const MAX_TRACKED_ISSUE_RULES = 10;

export const CLUSTER_TABLE_SETTINGS = {
    NAME: 'ai-agent-clustering-table',
    DEFAULT_SORT: {
        prop: 'numVisitors',
        order: 'descending',
    },
    PAGE_NUMBER: 1,
    PAGE_SIZE: 10,
};

export const TRACKED_USE_CASE_TABLE_SETTINGS = {
    NAME: 'ai-agent-tracked-use-case-table',
    DEFAULT_SORT: {
        prop: 'numVisitors',
        order: 'descending',
    },
    PAGE_NUMBER: 1,
    PAGE_SIZE: 10,
};

export const TRACKED_ISSUES_TABLE_SETTINGS = {
    NAME: 'ai-agent-tracked-issues-table',
    DEFAULT_SORT: {
        prop: 'createdAt',
        order: 'descending',
    },
    PAGE_NUMBER: 1,
    PAGE_SIZE: 50,
};

export const PROMPTS_TABLE_SETTINGS = {
    NAME: 'ai-agent-prompts-table',
    DEFAULT_SORT: {
        prop: 'browserTime',
        order: 'descending',
    },
    VISIBLE_COLUMNS: [],
    DEFAULT_PROMPTS_LIMIT: 1000,
};

export const DETECT_ISSUES_TABLE_SETTINGS = {
    NAME: 'ai-agent-detect-issues-table',
    DEFAULT_SORT: {
        prop: 'numVisitors',
        order: 'descending',
    },
    PAGE_NUMBER: 1,
    PAGE_SIZE: 10,
};

export const MESSAGE_TYPES = {
    USER: 'agentic:web:prompt',
    SYSTEM: 'agentic:web:agent_response',
    USER_REACTION: 'agentic:web:user_reaction',
    API_USER: 'agentic:api:prompt',
    API_SYSTEM: 'agentic:api:agent_response',
    API_USER_REACTION: 'agentic:api:user_reaction',
};

// Arrays for checking multiple event types
export const USER_MESSAGE_TYPES = [MESSAGE_TYPES.USER, MESSAGE_TYPES.API_USER];

export const SYSTEM_MESSAGE_TYPES = [MESSAGE_TYPES.SYSTEM, MESSAGE_TYPES.API_SYSTEM];

export const USER_REACTION_MESSAGE_TYPES = [MESSAGE_TYPES.USER_REACTION, MESSAGE_TYPES.API_USER_REACTION];

// Helper functions for checking event types
export const isUserMessageType = (eventType) => USER_MESSAGE_TYPES.includes(eventType);

export const isSystemMessageType = (eventType) => SYSTEM_MESSAGE_TYPES.includes(eventType);

export const isUserReactionMessageType = (eventType) => USER_REACTION_MESSAGE_TYPES.includes(eventType);

// Filter expressions for aggregations, for use with the agenticEvents source
export const MESSAGE_TYPE_FILTERS = {
    USER: `(eventType == "${MESSAGE_TYPES.USER}" || eventType == "${MESSAGE_TYPES.API_USER}")`,
    SYSTEM: `(eventType == "${MESSAGE_TYPES.SYSTEM}" || eventType == "${MESSAGE_TYPES.API_SYSTEM}")`,
    USER_REACTION: `(eventType == "${MESSAGE_TYPES.USER_REACTION}" || eventType == "${MESSAGE_TYPES.API_USER_REACTION}")`,
};

export const RAGE_PROMPT_USER_FILTER = `${MESSAGE_TYPE_FILTERS.USER} && ragePrompt != "None" && ragePrompt != ""`;

// For use with the singleEvents source (session replay)
export const MESSAGE_TYPE_FILTERS_BY_TYPE = {
    USER: `(type == "${MESSAGE_TYPES.USER}" || type == "${MESSAGE_TYPES.API_USER}")`,
    SYSTEM: `(type == "${MESSAGE_TYPES.SYSTEM}" || type == "${MESSAGE_TYPES.API_SYSTEM}")`,
    USER_REACTION: `(type == "${MESSAGE_TYPES.USER_REACTION}" || type == "${MESSAGE_TYPES.API_USER_REACTION}")`,
};

export const FEEDBACK_TYPES = {
    POSITIVE: 'positive',
    NEGATIVE: 'negative',
    UNREACT: 'unreact',
};

export const CONVERSATIONS_TABLE_SETTINGS = {
    NAME: 'ai-agent-conversations-table',
    DEFAULT_SORT: {
        prop: 'createdAt',
        order: 'descending',
    },
    PAGE_NUMBER: 1,
    PAGE_SIZE: 10,
    DEFAULT_CONVERSATION_LIMIT: 1000,
};

export const OVER_TIME_CHART_SETTINGS = {
    NAME: 'ai-agent-over-time-chart',
};

export const SEVERITY_THRESHOLDS = [
    { max: 1.0, level: 'critical' },
    { max: 2.0, level: 'major' },
    { max: 3.0, level: 'moderate' },
];

export const SEVERITY_LEVEL_TEXT_MAP = {
    critical: 'severityScore.Critical',
    major: 'severityScore.Major',
    moderate: 'severityScore.Moderate',
};
